import { IowsShoppingBagTypeEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import ServiceFetch from '../../../utilities/ServiceFetch.js';
import IowsShoppingBagErrorCodes from './IowsShoppingBagErrorCodes.js';
/**
 * This class handles CRUD operations for IOWS shopping cart API.
 */
export default class IowsShoppingBagService {
    constructor(options) {
        this.consumer = options.consumer;
        this.contract = options.contract;
        this.endpoint = options.endpoint;
    }
    /**
     * Adds items to shopping cart in IRW.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Array of shopping items.
     * @returns Promise object.
     */
    async addToCart(options) {
        return this.addToBag({
            userId: options.userId,
            items: options.items,
            shoppingBagType: IowsShoppingBagTypeEnum.cart
        });
    }
    /**
     * Adds items to shopping list in IRW.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Array of shopping items.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async addToList(options) {
        const cart = await this.addToBag({
            userId: options.userId,
            items: options.items,
            shoppingBagType: IowsShoppingBagTypeEnum.list,
            shoppingListName: options.shoppingListName
        });
        return {
            errorList: cart.errorList,
            listCount: cart.cartCount
        };
    }
    /**
     * Returns cart count for a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @returns Promise object.
     */
    async getCartCount(options) {
        const cartInfo = await this.getCartInfo({
            userId: options.userId,
            shoppingBagType: IowsShoppingBagTypeEnum.cart
        });
        if (cartInfo === null) {
            return 0;
        }
        return this.getCartCountFromCart({
            userId: options.userId,
            shoppingBagType: IowsShoppingBagTypeEnum.cart,
            cartId: cartInfo.id
        });
    }
    /**
     * Returns list count from a shopping list.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async getListCount(options) {
        const cartInfo = await this.getCartInfo({
            userId: options.userId,
            shoppingBagType: IowsShoppingBagTypeEnum.list,
            shoppingListName: options.shoppingListName
        });
        if (cartInfo === null) {
            return 0;
        }
        return this.getCartCountFromCart({
            userId: options.userId,
            shoppingBagType: IowsShoppingBagTypeEnum.list,
            cartId: cartInfo.id
        });
    }
    /**
     * Adds items to shopping cart in IRW.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Array of shopping items.
     * @param options.shoppingBagType Cart type. Valid values: "cart", "list".
     * @param options.shoppingListName List name.
     * @returns Promise object.
     */
    async addToBag(options) {
        const cartInfo = await this.getCartInfo(options);
        if (cartInfo !== null) {
            const updateOptions = Object.assign({}, options, { cartId: cartInfo.id });
            const updateInfo = await this.updateCartItems(updateOptions);
            if (updateInfo.cartCount === null) {
                updateInfo.cartCount = await this.getCartCountFromCart(updateOptions);
            }
            return updateInfo;
        }
        return await this.createCart(options);
    }
    /**
     * Returns cart count for a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.cartId Cart id.
     * @param options.shoppingBagType Cart type.
     * @returns Promise object.
     */
    async getCartCountFromCart(options) {
        const cart = await this.getCart(options);
        return cart.cartCount;
    }
    /**
     * Returns cart information (name and id) if a cart exists.
     * Returns null if the cart doesn't exist.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingBagType Cart type.
     * @param [options.shoppingListName] List name.
     * @returns Promise object.
     */
    async getCartInfo(options) {
        const response = await ServiceFetch.json(this.getEndpointUrl(options.userId, options.shoppingBagType), {
            headers: this.getHeaders(),
            credentials: 'include'
        });
        return this.parseCartInfo({
            response: response,
            shoppingListName: options.shoppingListName
        });
    }
    /**
     * Returns a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.cartId Cart id.
     * @param options.shoppingBagType Cart type.
     * @returns Promise object.
     */
    async getCart(options) {
        const url = this.getEndpointUrl(options.userId, options.shoppingBagType) + '/' + options.cartId;
        const response = await ServiceFetch.json(url, {
            headers: this.getHeaders(),
            credentials: 'include'
        });
        return {
            errorList: [],
            cartCount: this.calculateCartCountFromResponse(response)
        };
    }
    /**
     * Creates a shopping cart.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingBagType Cart type.
     * @param options.items Items to use.
     * @param [options.shoppingListName] List name.
     * @returns Promise object.
     */
    async createCart(options) {
        const url = this.getEndpointUrl(options.userId, options.shoppingBagType);
        const modelOptions = {
            userId: options.userId,
            items: options.items,
            shoppingBagType: options.shoppingBagType,
            shoppingListName: options.shoppingListName
        };
        const response = await ServiceFetch.json(url, {
            method: 'POST',
            headers: this.getHeaders(),
            credentials: 'include',
            body: 'list=' + JSON.stringify(this.createShoppingCartModel(modelOptions))
        });
        return this.parseResponse(response);
    }
    /**
     * Updates shopping cart items.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.shoppingBagType Cart type.
     * @param options.cartId Cart id.
     * @param options.items Items to use.
     * @returns Promise object.
     */
    async updateCartItems(options) {
        const endpointUrl = this.getEndpointUrl(options.userId, options.shoppingBagType);
        const url = endpointUrl + '/' + options.cartId + '/items';
        const response = await ServiceFetch.text(url, {
            method: 'PUT',
            headers: this.getHeaders(),
            credentials: 'include',
            body: 'list=' + JSON.stringify(this.createItemsAdjustmentListModel(options.items))
        });
        if (typeof response === 'string' && response.startsWith('{')) {
            return this.parseResponse(JSON.parse(response));
        }
        return {
            cartCount: null,
            errorList: []
        };
    }
    /**
     * Parses response.
     *
     * @param response Response.
     * @returns Promise object.
     */
    parseResponse(response) {
        const cartCount = this.calculateCartCountFromResponse(response);
        const parsed = {
            cartCount,
            errorList: []
        };
        if (typeof response === 'object' && response !== null && response.ErrorList) {
            const errorObject = response.ErrorList.Error;
            const errorList = Array.isArray(errorObject) ? errorObject : [errorObject];
            for (const error of errorList) {
                const errorMessage = error.ErrorMessage || {};
                const errorCode = error.ErrorCode || {};
                const message = errorMessage.$ ? errorMessage.$ : null;
                const code = errorCode.$ ? errorCode.$ : null;
                const items = [];
                if (error.ErrorAttributeList && Array.isArray(error.ErrorAttributeList.ErrorAttribute)) {
                    for (const attribute of error.ErrorAttributeList.ErrorAttribute) {
                        if (attribute.Value && attribute.Value.$) {
                            items.push(String(attribute.Value.$));
                        }
                    }
                }
                parsed.errorList.push({
                    code: IowsShoppingBagErrorCodes[code] || String(code),
                    message,
                    items
                });
            }
        }
        return parsed;
    }
    /**
     * Calculates cart count from data.
     *
     * @param response Data returned from the response.
     * @returns Cart count or error object.
     */
    calculateCartCountFromResponse(response) {
        const bag = response.ShoppingBag || {};
        bag.ShoppingBagSectionList = bag.ShoppingBagSectionList || {};
        bag.ShoppingBagSectionList.ShoppingBagSection =
            bag.ShoppingBagSectionList.ShoppingBagSection || {};
        bag.BagType = bag.BagType || { $: '' };
        const isCart = bag.BagType.$ === 'ONLINESHOPPINGCART';
        const isList = bag.BagType.$ === 'ONLINESHOPPINGLIST';
        const itemList = bag.ShoppingBagSectionList.ShoppingBagSection.ShoppingBagItemList;
        let cartCount = null;
        if ((isCart || isList) && itemList) {
            const items = itemList.ShoppingBagItem;
            if (!items) {
                return null;
            }
            const array = Array.isArray(items) ? items : [items];
            cartCount = 0;
            for (const item of array) {
                if (item.ItemQty) {
                    cartCount += item.ItemQty.$;
                }
            }
        }
        return cartCount;
    }
    /**
     * Parses cart data and returns cart info.
     *
     * @param options Options.
     * @param options.response Response.
     * @param [options.shoppingListName] List name.
     * @returns Parsed data.
     */
    parseCartInfo(options) {
        const { response, shoppingListName } = options;
        let cartInfo = null;
        if (response.ShoppingBag && response.ShoppingBag.BagId) {
            cartInfo = {
                id: response.ShoppingBag.BagId.$,
                name: response.ShoppingBag.BagName && response.ShoppingBag.BagName.$
                    ? response.ShoppingBag.BagName.$
                    : null,
                type: response.ShoppingBag.BagType && response.ShoppingBag.BagType.$
                    ? response.ShoppingBag.BagType.$.toLowerCase()
                    : null
            };
        }
        else if (response.ShoppingBagRefList && response.ShoppingBagRefList.ShoppingBagRef) {
            if (Array.isArray(response.ShoppingBagRefList.ShoppingBagRef)) {
                for (const ref of response.ShoppingBagRefList.ShoppingBagRef) {
                    if (ref.BagId &&
                        (!ref.BagName ||
                            !ref.BagName.$ ||
                            !ref.BagType ||
                            !ref.BagType.$ ||
                            ref.BagName.$ === shoppingListName)) {
                        cartInfo = {
                            id: ref.BagId.$,
                            name: ref.BagName && ref.BagName.$ ? ref.BagName.$ : null,
                            type: ref.BagType && ref.BagType.$ ? ref.BagType.$.toLowerCase() : null
                        };
                        break;
                    }
                }
            }
            else if (response.ShoppingBagRefList.ShoppingBagRef.BagId) {
                const ref = response.ShoppingBagRefList.ShoppingBagRef;
                cartInfo = {
                    id: ref.BagId?.$ ?? null,
                    name: ref.BagName && ref.BagName.$ ? ref.BagName.$ : null,
                    type: ref.BagType && ref.BagType.$ ? ref.BagType.$.toLowerCase() : null
                };
            }
        }
        if (cartInfo?.type && cartInfo?.name && cartInfo?.name !== shoppingListName) {
            return null;
        }
        return cartInfo;
    }
    /**
     * Generates a shopping cart model to be used as JSON.
     *
     * @param options Options.
     * @param options.userId User ID.
     * @param options.items Items to use.
     * @param options.shoppingBagType Cart type.
     * @param [options.shoppingListName] List name.
     * @param [options.cartId] Cart id.
     * @param options.bagId ...
     * @returns Shopping cart model.
     */
    createShoppingCartModel(options) {
        const shoppingListName = options.shoppingListName || options.shoppingBagType;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const json = {
            ShoppingBag: {
                BagType: {
                    $: options.shoppingBagType
                },
                BagSource: {
                    $: 'IRW'
                },
                BagName: {
                    $: shoppingListName
                },
                CustomerSource: {
                    $: 'IRW'
                },
                CustomerId: {
                    $: options.userId
                },
                CreatedDateTime: {
                    $: this.getCreatedTime()
                },
                ShoppingBagSectionList: [
                    {
                        ShoppingBagSection: {
                            ShoppingBagItemList: {
                                ShoppingBagItem: []
                            }
                        }
                    }
                ],
                '@xmlns': {
                    $: 'ikea.com/cem/iows/ShoppingBagService/2.0/'
                }
            }
        };
        const list = json.ShoppingBag.ShoppingBagSectionList[0].ShoppingBagSection.ShoppingBagItemList
            .ShoppingBagItem;
        for (const item of options.items) {
            if (typeof item.id !== 'string' ||
                typeof item.type !== 'string' ||
                typeof item.quantity !== 'number') {
                throw new ServiceException('Item was not in a valid format. Please use this format: { id: 123456, type: "ART", quantity: 1 }');
            }
            list.push({
                ItemType: {
                    $: item.type.toUpperCase()
                },
                ItemNo: {
                    $: item.id
                },
                ItemQty: {
                    $: item.quantity
                }
            });
        }
        if (options.bagId) {
            json.BagId = {
                $: options.bagId
            };
        }
        return json;
    }
    /**
     * Generates a shopping cart adjustment list model to be used as JSON.
     *
     * @param items Items to use.
     * @returns Shopping cart model.
     */
    createItemsAdjustmentListModel(items) {
        const json = {
            ShoppingBagAdjustmentItemList: {
                ShoppingBagAdjustmentItem: [],
                '@xmlns': {
                    $: 'ikea.com/cem/iows/ShoppingBagService/2.0/'
                }
            }
        };
        const list = json.ShoppingBagAdjustmentItemList.ShoppingBagAdjustmentItem;
        for (const item of items) {
            if (typeof item.id !== 'string' ||
                typeof item.type !== 'string' ||
                typeof item.quantity !== 'number') {
                throw new ServiceException('Item was not in a valid format. Please use this format: { id: 123456, type: "ART", quantity: 1 }');
            }
            list.push({
                ItemType: {
                    $: item.type.toUpperCase()
                },
                ItemNo: {
                    $: item.id
                },
                ItemQty: {
                    $: item.quantity
                },
                SectionId: {
                    $: 0
                }
            });
        }
        return json;
    }
    /**
     * Returns headers to be used by requests.
     *
     * @returns Object as key value pairs of headers.
     */
    getHeaders() {
        return {
            accept: 'application/vnd.ikea.iows+json; version=2.0',
            'auth-token': this.consumer + '-' + this.contract,
            contract: this.contract,
            consumer: this.consumer,
            'content-type': 'application/x-www-form-urlencoded'
        };
    }
    /**
     * Returns endpoint URL.
     *
     * @param userId User id.
     * @param shoppingBagType Cart type. Valid values: "cart", "list".
     * @returns Endpoint url.
     */
    getEndpointUrl(userId, shoppingBagType) {
        return this.endpoint
            .replace('{userId}', userId)
            .replace('{bagType}', shoppingBagType.toLowerCase());
    }
    /**
     * Returns a date ISO string of the current time.
     *
     * @returns Date ISO string.
     */
    getCreatedTime() {
        return new Date().toISOString();
    }
}
//# sourceMappingURL=IowsShoppingBagService.js.map